/**
 * StatusFilter Component
 * Multi-select filter for OKR statuses
 */

import React from 'react';

interface StatusFilterProps {
  availableStatuses: string[];
  selectedStatuses: string[];
  onChange: (statuses: string[]) => void;
}

export const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  draft: { label: 'Draft', color: 'bg-stone-100 text-stone-700' },
  active: { label: 'Active', color: 'bg-primary-100 text-primary-700' },
  on_track: { label: 'On Track', color: 'bg-success-100 text-success-700' },
  at_risk: { label: 'At Risk', color: 'bg-warning-100 text-warning-700' },
  off_track: { label: 'Off Track', color: 'bg-danger-100 text-danger-700' },
  completed: { label: 'Completed', color: 'bg-success-100 text-success-700' },
  // S1-g. `availableStatuses` comes from okr_filter_metadata, i.e. statuses actually present
  // in the tenant's data — so `closed` has always been offered here and has always rendered
  // as the raw string `closed`.
  closed: { label: 'Closed', color: 'bg-stone-100 text-stone-600' },
  deleted: { label: 'Deleted', color: 'bg-stone-100 text-stone-500' },
  cancelled: { label: 'Cancelled', color: 'bg-stone-100 text-stone-600' },
};

export const StatusFilter: React.FC<StatusFilterProps> = ({
  availableStatuses,
  selectedStatuses,
  onChange,
}) => {
  const handleToggle = (status: string) => {
    if (selectedStatuses.includes(status)) {
      onChange(selectedStatuses.filter((s) => s !== status));
    } else {
      onChange([...selectedStatuses, status]);
    }
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-stone-700">
        Status
      </label>
      <div className="space-y-1.5">
        {availableStatuses.map((status) => {
          const config = STATUS_CONFIG[status] || { label: status, color: 'bg-stone-100 text-stone-700' };
          const isSelected = selectedStatuses.includes(status);

          return (
            <label
              key={status}
              className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-stone-50 cursor-pointer transition-colors"
            >
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => handleToggle(status)}
                className="w-4 h-4 text-primary-600 border-stone-300 rounded focus:ring-2 focus:ring-primary-500"
              />
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${config.color}`}>
                {config.label}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
};
